import { Application } from '../application/application';
import { InternalStorage, cacheClean } from './storage/storage.util';

type CacheContainer = {
    ttl: number | null;
    value: any;
}

export class InternalCacheStorage<T>{

    static async clearCache() {
        await this.#internalStorage.clear();
        await cacheClean();
    }

    static readonly #internalStorage: InternalStorage = new InternalStorage({
        name: Application.STORAGE_NAME,
        storeName: Application.CACHE_STORAGE_NAME
    });

    readonly #cacheName: string;

    #cache: Map<string, CacheContainer> = new Map();
    #initialization: Promise<void>;

    constructor(name: string) {
        this.#cacheName = name;
        this.#initialization = this.#init();
    }

    async #init() {
        const cache = await InternalCacheStorage.#internalStorage.get(this.#cacheName);
        if (cache)
            this.#cache = new Map(Object.entries(cache));
        else
            this.#cache = new Map();
    }

    async #updateStorage() {
        await InternalCacheStorage.#internalStorage.set(this.#cacheName, Object.fromEntries(this.#cache));
    }

    async set(key: string, value: T, ttl?: number): Promise<void> {
        await this.#initialization;
        this.#cache.set(key, {
            value,
            ttl: ttl ? Date.now() + ttl : null
        });
        await this.#updateStorage();
    }

    // @ts-ignore
    async get<TReturn = T>(key: string): Promise<TReturn | undefined> {
        await this.#initialization;
        const content = this.#cache.get(key);
        if (typeof content === 'undefined')
            return undefined;

        if (content.ttl && content.ttl < Date.now())
            return undefined;

        return content.value;
    }

    async delete(key: string): Promise<void> {
        await this.#initialization;
        this.#cache.delete(key);
        await this.#updateStorage();
    }

    async has(key: string): Promise<boolean> {
        const now = Date.now();
        return this.#cache.has(key) && (this.#cache.get(key)?.ttl ?? now) >= now;
    }

    async clear(): Promise<void> {
        this.#cache.clear();
        await InternalCacheStorage.#internalStorage.remove(this.#cacheName);
    }

}